import type {
  TopologyPathEndpointReferenceschema,
  TopologyPathEndpointSearchRequest,
  TopologyPathEndpointSearchResponse,
  TopologyPathTraceRequest,
  TopologyPathTraceResponse
} from '@app/types/sdk';
import { TopologyPathTraceResponseStatusEnum } from '@app/types/sdk';

type DemoSide = 'source' | 'destination';

type DemoEndpoint = {
  kind: NonNullable<TopologyPathEndpointSearchResponse['items'][number]['kind']>;
  displayName: string;
  summary: string;
  reference: TopologyPathEndpointReferenceschema;
  site: { id: string; name: string };
  searchTokens: string[];
  side: DemoSide;
  sourceOrder: number;
  destinationOrder: number;
};

const DEFAULT_LIMIT = 12;
const CLOUD_SOURCE_SITE_ID = 'site-aws-us-east-1';
const CLOUD_SOURCE_SITE_NAME = 'AWS us-east-1';
const CLOUD_DESTINATION_SITE_ID = 'site-aws-ap-south-1';
const CLOUD_DESTINATION_SITE_NAME = 'AWS ap-south-1';
const ON_PREM_TRACE_CLOUD_SITE_ID = '7c9de981-6392-46d2-92b6-ded24674e318';
const ON_PREM_TRACE_CLOUD_SITE_NAME = 'AWS us-east-1';
const ON_PREM_SOURCE_SITE_ID = 'db90a089-c3ec-42d3-9909-e28bbbd0908a';
const ON_PREM_SOURCE_SITE_NAME = 'On-prem site';

type StableMockNode = [id: string, displayName: string, level: string, properties?: Record<string, unknown>];
type StableMockEdge = [id: string, fromId: string, toId: string, relation: string, directed?: boolean, properties?: Record<string, unknown>];
type StableMockHop = { node: string; ingress?: string; egress?: string };

const stablePathProperties = (pathId: string, properties: Record<string, unknown> = {}): Record<string, unknown> => ({
  ...properties,
  topologyPath: { pathIds: [pathId], ranks: [1], primary: true, shared: false }
});

const stableTraceResponse = (
  pathId: string,
  summary: string,
  rawNodes: StableMockNode[],
  rawEdges: StableMockEdge[],
  rawHops: StableMockHop[]
): TopologyPathTraceResponse => {
  const nodes = rawNodes.map(([id, displayName, level, properties = {}]) => ({
    id,
    displayName,
    labels: { level, ...(id.includes('ref:aws.') || id.includes('ref:derived:vpn_connection:') ? { domain: 'aws' } : {}) },
    properties: stablePathProperties(pathId, properties)
  }));
  const edges = rawEdges.map(([id, fromId, toId, relation, directed = false, properties = {}]) => ({
    id,
    fromId,
    toId,
    directed,
    labels: { relation },
    properties: stablePathProperties(pathId, properties)
  }));
  const hops = rawHops.map((hop, index) => ({
    index,
    node: { id: hop.node },
    ...(hop.ingress ? { ingress: { id: hop.ingress } } : {}),
    ...(hop.egress ? { egress: { id: hop.egress } } : {})
  }));
  const path = { pathId, completionStatus: 'complete', hops };
  return {
    status: TopologyPathTraceResponseStatusEnum.Ready,
    summary,
    path: { status: 'ok', paths: [path] },
    patch: {
      ops: [
        {
          op: 'upsert_layer',
          layer: {
            id: `mock-layer:${pathId}`,
            graphOverlay: {
              id: `mock-related-view:${pathId}`,
              graph: {
                id: `mock-graph:${pathId}`,
                meta: { schema: 'topology.path.related_view.v1', pathId, pathIds: [pathId], path: { pathId, pathIds: [pathId], paths: [path] } },
                nodes,
                edges
              }
            }
          }
        }
      ]
    }
  } as unknown as TopologyPathTraceResponse;
};

const cloudToCloudPathId = 'mock:cloud-to-cloud';
const cloudSourceENI = 'ref:aws.eni:774515140353:us-east-1:eni-0eb471a06969c0422';
const cloudIntermediateTGW = 'ref:aws.tgw:774515140353:ap-northeast-1:tgw-010933a1f4e4b283f';
const cloudIntermediateENI = 'ref:aws.eni:774515140353:ap-northeast-1:eni-0d9974037782fc869';
const cloudDestinationENI = 'ref:aws.eni:774515140353:ap-south-1:eni-0983ca104a0ae0395';

// These are intentionally small renderer fixtures, not archived API captures.
export const cloudToCloudTgwPeeringMockResponse = stableTraceResponse(
  cloudToCloudPathId,
  'Mock cloud-to-cloud trace through TGW peering.',
  [
    ['ref:aws.ec2_instance:774515140353:us-east-1:i-source', 'source EC2', 'device'],
    [cloudSourceENI, 'eni-source', 'interface', { aws: { privateIpAddress: '10.10.1.58' } }],
    ['ref:aws.subnet:774515140353:us-east-1:subnet-source', 'source-subnet', 'subnet', { aws: { cidrBlock: '10.10.1.0/24' } }],
    ['ref:aws.tgw:774515140353:us-east-1:tgw-source', 'tgw-source', 'device'],
    ['ref:aws.eni:774515140353:us-east-1:eni-tgw-source', 'eni-tgw-source', 'interface'],
    [cloudIntermediateTGW, 'tgw-intermediate', 'device'],
    [cloudIntermediateENI, 'eni-tgw-intermediate', 'interface'],
    ['ref:aws.subnet:774515140353:ap-northeast-1:subnet-transit', 'transit-subnet', 'subnet', { aws: { cidrBlock: '10.20.0.0/28' } }],
    ['ref:aws.tgw:774515140353:ap-south-1:tgw-destination', 'tgw-destination', 'device'],
    ['ref:aws.eni:774515140353:ap-south-1:eni-tgw-destination', 'eni-tgw-destination', 'interface'],
    ['ref:aws.subnet:774515140353:ap-south-1:subnet-destination', 'destination-subnet', 'subnet', { aws: { cidrBlock: '10.40.0.0/24' } }],
    ['ref:aws.ec2_instance:774515140353:ap-south-1:i-destination', 'destination EC2', 'device'],
    [cloudDestinationENI, 'eni-destination', 'interface', { aws: { privateIpAddress: '10.40.0.199' } }]
  ],
  [
    ['source-owner-eni', 'ref:aws.ec2_instance:774515140353:us-east-1:i-source', cloudSourceENI, 'contains'],
    ['source-tgw-eni', 'ref:aws.tgw:774515140353:us-east-1:tgw-source', 'ref:aws.eni:774515140353:us-east-1:eni-tgw-source', 'contains'],
    ['intermediate-tgw-eni', cloudIntermediateTGW, cloudIntermediateENI, 'contains'],
    ['destination-tgw-eni', 'ref:aws.tgw:774515140353:ap-south-1:tgw-destination', 'ref:aws.eni:774515140353:ap-south-1:eni-tgw-destination', 'contains'],
    ['destination-owner-eni', 'ref:aws.ec2_instance:774515140353:ap-south-1:i-destination', cloudDestinationENI, 'contains']
  ],
  [
    { node: cloudSourceENI, egress: cloudSourceENI },
    { node: 'ref:aws.subnet:774515140353:us-east-1:subnet-source', ingress: cloudSourceENI, egress: 'ref:aws.eni:774515140353:us-east-1:eni-tgw-source' },
    { node: 'ref:aws.tgw:774515140353:us-east-1:tgw-source', ingress: 'ref:aws.eni:774515140353:us-east-1:eni-tgw-source' },
    { node: cloudIntermediateTGW, egress: cloudIntermediateENI },
    { node: 'ref:aws.subnet:774515140353:ap-northeast-1:subnet-transit', ingress: cloudIntermediateENI, egress: cloudIntermediateENI },
    { node: cloudIntermediateTGW, ingress: cloudIntermediateENI },
    { node: 'ref:aws.tgw:774515140353:ap-south-1:tgw-destination', egress: 'ref:aws.eni:774515140353:ap-south-1:eni-tgw-destination' },
    { node: 'ref:aws.subnet:774515140353:ap-south-1:subnet-destination', ingress: 'ref:aws.eni:774515140353:ap-south-1:eni-tgw-destination', egress: cloudDestinationENI },
    { node: cloudDestinationENI, ingress: cloudDestinationENI }
  ]
);

const onPremToCloudPathId = 'mock:onprem-to-cloud';
const pan = 'ref:inventory.device:topo-a2-pan-a';
const panVRF = 'ref:derived:ri:topo-a2-pan-a#CAMPUS-VR';
const panTunnel = 'ref:derived:interface:l3:topo-a2-pan-a#tunnel.1';
const insideSubnet = 'ref:derived:l3:subnet:169.254.203.32/30';
const cloudTGW = 'ref:aws.tgw:774515140353:us-east-1:tgw-09c44bbe01c05c66e';
const cloudVPN = 'ref:aws.vpn_connection:774515140353:us-east-1:vpn-0889828ec4899da42';
const cloudTunnel = 'ref:derived:vpn_connection:774515140353:us-east-1:vpn-0889828ec4899da42#tunnel-1';
const cloudTGWENI = 'ref:aws.eni:774515140353:us-east-1:eni-tgw-handoff';

export const onPremToCloudMockResponse = stableTraceResponse(
  onPremToCloudPathId,
  'Mock on-prem-to-cloud trace through the selected tunnel.',
  [
    ['ref:derived:l3:subnet:10.110.120.0/24', '10.110.120.0/24', 'subnet'],
    ['ref:inventory.device:topo-a2-core1', 'TOPO-A2-CORE1', 'device'],
    ['ref:derived:interface:l3:topo-a2-core1#Vl120', 'Vl120', 'interface', { addresses: ['10.110.120.2/24'] }],
    ['ref:derived:interface:l3:topo-a2-core1#Vl531', 'Vl531', 'interface', { addresses: ['10.255.31.1/30'] }],
    ['ref:derived:l3:subnet:10.255.31.0/30', '10.255.31.0/30', 'subnet'],
    [pan, 'TOPO-A2-PAN-A', 'device'],
    [panVRF, 'CAMPUS-VR', 'vrf'],
    [panTunnel, 'tunnel.1', 'interface', { addresses: ['169.254.203.34/30'], 'if.kind': 'tunnel' }],
    [insideSubnet, '169.254.203.32/30', 'subnet'],
    [cloudTGW, 'reap-demo-aws-tgw', 'device'],
    [cloudVPN, 'vpn-0889828ec4899da42', 'vpn_connection'],
    [cloudTunnel, 'tunnel-1', 'vpn_endpoint', { presentation: { rows: [{ key: 'insideIp', value: '169.254.203.33' }] } }],
    [cloudTGWENI, 'eni-tgw-handoff', 'interface'],
    ['ref:aws.subnet:774515140353:us-east-1:subnet-destination', 'connector-subnet', 'subnet', { aws: { cidrBlock: '10.10.0.192/27' } }],
    ['ref:aws.ec2_instance:774515140353:us-east-1:i-destination', 'connector EC2', 'device'],
    ['ref:aws.eni:774515140353:us-east-1:eni-00503ba4ede8e7b24', 'eni-destination', 'interface', { aws: { privateIpAddress: '10.10.0.145' } }]
  ],
  [
    ['core-ingress', 'ref:inventory.device:topo-a2-core1', 'ref:derived:interface:l3:topo-a2-core1#Vl120', 'contains'],
    ['core-egress', 'ref:inventory.device:topo-a2-core1', 'ref:derived:interface:l3:topo-a2-core1#Vl531', 'contains'],
    ['pan-vrf', pan, panVRF, 'contains'],
    ['vrf-tunnel', panVRF, panTunnel, 'contains'],
    ['tgw-vpn', cloudTGW, cloudVPN, 'contains'],
    ['vpn-tunnel', cloudVPN, cloudTunnel, 'contains'],
    ['tgw-eni', cloudTGW, cloudTGWENI, 'contains'],
    ['destination-owner-eni', 'ref:aws.ec2_instance:774515140353:us-east-1:i-destination', 'ref:aws.eni:774515140353:us-east-1:eni-00503ba4ede8e7b24', 'contains'],
    ['source-core', 'ref:derived:l3:subnet:10.110.120.0/24', 'ref:derived:interface:l3:topo-a2-core1#Vl120', 'forwarding', true],
    ['core-transit', 'ref:derived:interface:l3:topo-a2-core1#Vl531', 'ref:derived:l3:subnet:10.255.31.0/30', 'forwarding', true],
    ['transit-pan', 'ref:derived:l3:subnet:10.255.31.0/30', panTunnel, 'forwarding', true],
    ['pan-inside', panTunnel, insideSubnet, 'forwarding', true]
  ],
  [
    { node: 'ref:derived:l3:subnet:10.110.120.0/24', egress: 'ref:derived:interface:l3:topo-a2-core1#Vl120' },
    { node: 'ref:inventory.device:topo-a2-core1', ingress: 'ref:derived:interface:l3:topo-a2-core1#Vl120', egress: 'ref:derived:interface:l3:topo-a2-core1#Vl531' },
    { node: 'ref:derived:l3:subnet:10.255.31.0/30', ingress: 'ref:derived:interface:l3:topo-a2-core1#Vl531', egress: panTunnel },
    { node: pan, ingress: panTunnel, egress: panTunnel },
    { node: insideSubnet, ingress: panTunnel, egress: cloudTunnel },
    { node: cloudTGW, ingress: cloudTunnel, egress: cloudTGWENI },
    { node: 'ref:aws.subnet:774515140353:us-east-1:subnet-destination', ingress: cloudTGWENI, egress: 'ref:aws.eni:774515140353:us-east-1:eni-00503ba4ede8e7b24' },
    { node: 'ref:aws.eni:774515140353:us-east-1:eni-00503ba4ede8e7b24', ingress: 'ref:aws.eni:774515140353:us-east-1:eni-00503ba4ede8e7b24' }
  ]
);

const cloudToOnPremPathId = 'mock:cloud-to-onprem';
export const cloudToOnPremMockResponse = stableTraceResponse(
  cloudToOnPremPathId,
  'Mock cloud-to-on-prem trace through the selected VPN tunnel.',
  [
    ['ref:aws.ec2_instance:774515140353:us-east-1:i-source', 'source EC2', 'device'],
    [cloudSourceENI, 'eni-source', 'interface', { aws: { privateIpAddress: '10.10.1.58' } }],
    ['ref:aws.subnet:774515140353:us-east-1:subnet-telemetry', 'telemetry-subnet', 'subnet', { aws: { cidrBlock: '10.10.1.32/27' } }],
    [cloudTGW, 'reap-demo-aws-tgw', 'device'],
    [cloudVPN, 'vpn-0889828ec4899da42', 'vpn_connection'],
    [cloudTunnel, 'tunnel-1', 'vpn_endpoint', { presentation: { rows: [{ key: 'insideIp', value: '169.254.203.33' }] } }],
    [cloudTGWENI, 'eni-tgw-handoff', 'interface'],
    [insideSubnet, '169.254.203.32/30', 'subnet'],
    [pan, 'TOPO-A2-PAN-A', 'device'],
    [panVRF, 'CAMPUS-VR', 'vrf'],
    [panTunnel, 'tunnel.1', 'interface', { addresses: ['169.254.203.34/30'], 'if.kind': 'tunnel' }],
    ['ref:derived:l3:subnet:10.255.31.0/30', '10.255.31.0/30', 'subnet'],
    ['ref:inventory.device:topo-a2-core1', 'TOPO-A2-CORE1', 'device'],
    ['ref:derived:interface:l3:topo-a2-core1#Vl531', 'Vl531', 'interface', { addresses: ['10.255.31.1/30'] }],
    ['ref:derived:l3:subnet:10.110.120.0/24', '10.110.120.0/24', 'subnet']
  ],
  [
    ['source-owner-eni', 'ref:aws.ec2_instance:774515140353:us-east-1:i-source', cloudSourceENI, 'contains'],
    ['tgw-vpn', cloudTGW, cloudVPN, 'contains'],
    ['vpn-tunnel', cloudVPN, cloudTunnel, 'contains'],
    ['tgw-eni', cloudTGW, cloudTGWENI, 'contains'],
    ['pan-vrf', pan, panVRF, 'contains'],
    ['vrf-tunnel', panVRF, panTunnel, 'contains'],
    ['core-interface', 'ref:inventory.device:topo-a2-core1', 'ref:derived:interface:l3:topo-a2-core1#Vl531', 'contains'],
    ['inside-pan-tunnel', insideSubnet, panTunnel, 'forwarding', true],
    ['pan-transit', panTunnel, 'ref:derived:l3:subnet:10.255.31.0/30', 'forwarding', true],
    ['transit-core', 'ref:derived:l3:subnet:10.255.31.0/30', 'ref:derived:interface:l3:topo-a2-core1#Vl531', 'forwarding', true],
    ['core-destination', 'ref:derived:interface:l3:topo-a2-core1#Vl531', 'ref:derived:l3:subnet:10.110.120.0/24', 'forwarding', true]
  ],
  [
    { node: cloudSourceENI, egress: cloudSourceENI },
    { node: 'ref:aws.subnet:774515140353:us-east-1:subnet-telemetry', ingress: cloudSourceENI, egress: cloudTGWENI },
    { node: cloudTGW, ingress: cloudTGWENI, egress: cloudTunnel },
    { node: insideSubnet, ingress: cloudTunnel, egress: panTunnel },
    { node: pan, ingress: panTunnel, egress: panTunnel },
    { node: 'ref:derived:l3:subnet:10.255.31.0/30', ingress: panTunnel, egress: 'ref:derived:interface:l3:topo-a2-core1#Vl531' },
    { node: 'ref:inventory.device:topo-a2-core1', ingress: 'ref:derived:interface:l3:topo-a2-core1#Vl531', egress: 'ref:derived:interface:l3:topo-a2-core1#Vl531' },
    { node: 'ref:derived:l3:subnet:10.110.120.0/24', ingress: 'ref:derived:interface:l3:topo-a2-core1#Vl531' }
  ]
);

const capturedCloudToCloudTraceResponse = cloudToCloudTgwPeeringMockResponse;
const capturedCloudToOnPremTraceResponse = cloudToOnPremMockResponse;
const capturedOnPremToCloudTraceResponse = onPremToCloudMockResponse;

const normalize = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';

const displayText = (reference: TopologyPathEndpointReferenceschema | undefined): string =>
  (typeof reference?.displayName === 'string' && reference.displayName.trim()) ||
  (typeof reference?.text === 'string' && reference.text.trim()) ||
  (typeof reference?.id === 'string' && reference.id.trim()) ||
  'endpoint';

const clampLimit = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_LIMIT;
  }
  return Math.max(1, Math.min(DEFAULT_LIMIT, Math.floor(parsed)));
};

const DEMO_ENDPOINTS: DemoEndpoint[] = [
  {
    kind: 'ip_address',
    displayName: '10.110.120.3',
    summary: 'On-prem source IP • historical on-prem → AWS capture',
    reference: {
      kind: 'ip_address',
      id: '10.110.120.3',
      displayName: '10.110.120.3',
      text: '10.110.120.3',
      scopeId: ON_PREM_SOURCE_SITE_ID
    },
    site: { id: ON_PREM_SOURCE_SITE_ID, name: ON_PREM_SOURCE_SITE_NAME },
    searchTokens: ['10.110.120.3', 'on-prem source', 'historical tunnel'],
    side: 'source',
    sourceOrder: 1,
    destinationOrder: 20
  },
  {
    kind: 'ip_address',
    displayName: '10.10.0.145',
    summary: 'Destination ENI eni-00503ba4ede8e7b24 • AWS us-east-1',
    reference: {
      kind: 'ip_address',
      id: '9c43f6e5-c471-49c6-bd69-51bad3a1aa44',
      displayName: '10.10.0.145',
      text: '10.10.0.145',
      scopeId: ON_PREM_TRACE_CLOUD_SITE_ID
    },
    site: { id: ON_PREM_TRACE_CLOUD_SITE_ID, name: ON_PREM_TRACE_CLOUD_SITE_NAME },
    searchTokens: ['10.10.0.145', 'eni-00503ba4ede8e7b24', 'aws destination', 'historical tunnel'],
    side: 'destination',
    sourceOrder: 20,
    destinationOrder: 1
  },
  {
    kind: 'ip_address',
    displayName: '10.10.1.58',
    summary: 'Source ENI eni-0eb471a06969c0422 • AWS us-east-1',
    reference: {
      kind: 'ip_address',
      id: '47ef55b1-9c3b-48c3-884e-ab67a0ee5fea',
      displayName: '10.10.1.58',
      text: '10.10.1.58',
      scopeId: CLOUD_SOURCE_SITE_ID
    },
    site: { id: CLOUD_SOURCE_SITE_ID, name: CLOUD_SOURCE_SITE_NAME },
    searchTokens: ['10.10.1.58', 'eni-0eb471a06969c0422', 'source eni', 'aws us-east-1'],
    side: 'source',
    sourceOrder: 2,
    destinationOrder: 20
  },
  {
    kind: 'ip_address',
    displayName: '10.110.120.3',
    summary: 'On-prem destination IP • historical AWS → on-prem capture',
    reference: {
      kind: 'ip_address',
      id: '10.110.120.3',
      displayName: '10.110.120.3',
      text: '10.110.120.3',
      scopeId: ON_PREM_SOURCE_SITE_ID
    },
    site: { id: ON_PREM_SOURCE_SITE_ID, name: ON_PREM_SOURCE_SITE_NAME },
    searchTokens: ['10.110.120.3', 'on-prem destination', 'historical tunnel'],
    side: 'destination',
    sourceOrder: 20,
    destinationOrder: 2
  },
  {
    kind: 'ip_address',
    displayName: '10.40.0.199',
    summary: 'Destination ENI eni-0983ca104a0ae0395 • AWS ap-south-1',
    reference: {
      kind: 'ip_address',
      id: 'd25f9879-57a2-40fa-b19c-d29391b1b557',
      displayName: '10.40.0.199',
      text: '10.40.0.199',
      scopeId: CLOUD_DESTINATION_SITE_ID
    },
    site: { id: CLOUD_DESTINATION_SITE_ID, name: CLOUD_DESTINATION_SITE_NAME },
    searchTokens: ['10.40.0.199', 'eni-0983ca104a0ae0395', 'destination eni', 'aws ap-south-1'],
    side: 'destination',
    sourceOrder: 20,
    destinationOrder: 1
  }
];

const candidatesForSide = (side: TopologyPathEndpointSearchRequest['side']): DemoEndpoint[] =>
  DEMO_ENDPOINTS.filter((endpoint) => endpoint.side === side);

const scoreEndpoint = (
  endpoint: DemoEndpoint,
  query: string
): { score: number; matchType: TopologyPathEndpointSearchResponse['items'][number]['matchType'] } | null => {
  let bestScore = 0;
  let bestMatchType: TopologyPathEndpointSearchResponse['items'][number]['matchType'] = undefined;
  endpoint.searchTokens.forEach((token) => {
    const normalizedToken = normalize(token);
    if (!normalizedToken) {
      return;
    }
    if (normalizedToken === query) {
      bestScore = Math.max(bestScore, 100);
      bestMatchType = endpoint.reference.id === query ? 'exact_id' : query.includes('.') ? 'exact_ip' : 'exact_name';
      return;
    }
    if (normalizedToken.startsWith(query)) {
      bestScore = Math.max(bestScore, 80);
      bestMatchType = 'prefix';
      return;
    }
    if (normalizedToken.includes(query)) {
      bestScore = Math.max(bestScore, 55);
      bestMatchType = 'contains';
    }
  });
  return bestScore > 0 ? { score: bestScore, matchType: bestMatchType ?? 'contains' } : null;
};

const buildSearchItem = (
  endpoint: DemoEndpoint,
  score: number,
  matchType: TopologyPathEndpointSearchResponse['items'][number]['matchType']
): TopologyPathEndpointSearchResponse['items'][number] => ({
  kind: endpoint.kind,
  displayName: endpoint.displayName,
  summary: endpoint.summary,
  matchType,
  score,
  site: endpoint.site,
  reference: endpoint.reference
});

const endpointFromReference = (reference: TopologyPathEndpointReferenceschema | undefined): DemoEndpoint | null => {
  if (!reference) {
    return null;
  }
  const id = normalize(reference.id);
  const text = normalize(reference.text);
  const name = normalize(reference.displayName);
  return (
    DEMO_ENDPOINTS.find((endpoint) => {
      const endpointId = normalize(endpoint.reference.id);
      const endpointText = normalize(endpoint.reference.text);
      const endpointName = normalize(endpoint.displayName);
      return Boolean(
        (id && endpointId === id) ||
          (text && (endpointText === text || endpoint.searchTokens.some((token) => normalize(token) === text))) ||
          (name && endpointName === name)
      );
    }) ?? null
  );
};

export const searchMockTopologyPathEndpoints = (
  _orgId: string,
  request: TopologyPathEndpointSearchRequest
): TopologyPathEndpointSearchResponse => {
  const side = request.side === 'destination' ? 'destination' : 'source';
  const limit = clampLimit(request.limit);
  const query = normalize(request.query);
  const sourceCandidates = candidatesForSide(side);

  if (!query) {
    const items = [...sourceCandidates]
      .sort((left, right) =>
        side === 'source' ? left.sourceOrder - right.sourceOrder || left.displayName.localeCompare(right.displayName) : left.destinationOrder - right.destinationOrder || left.displayName.localeCompare(right.displayName)
      )
      .slice(0, limit)
      .map((endpoint) => buildSearchItem(endpoint, 50, request.zeroStateMode === 'recent' ? 'recent' : 'pinned'));

    return {
      status: 'ok',
      items,
      coverage: {
        scopeMode: request.zeroStateMode === 'recent' ? 'zero_state_recent' : 'zero_state_bounded_defaults',
        itemLimit: limit
      }
    };
  }

  const items = sourceCandidates
    .map((endpoint) => {
      const match = scoreEndpoint(endpoint, query);
      if (!match) {
        return null;
      }
      const sideBias = side === 'source' ? Math.max(0, 10 - endpoint.sourceOrder) : Math.max(0, 10 - endpoint.destinationOrder);
      return buildSearchItem(endpoint, match.score + sideBias, match.matchType);
    })
    .filter((item): item is TopologyPathEndpointSearchResponse['items'][number] => Boolean(item))
    .sort((left, right) => (right.score ?? 0) - (left.score ?? 0) || left.displayName.localeCompare(right.displayName))
    .slice(0, limit);

  return {
    status: 'ok',
    items,
    coverage: {
      scopeMode: 'org_wide_prefix_bounded',
      itemLimit: limit
    }
  };
};

export const traceMockTopologyPath = (_orgId: string, request: TopologyPathTraceRequest): TopologyPathTraceResponse => {
  const sourceEndpoint = endpointFromReference(request.source);
  const destinationEndpoint = endpointFromReference(request.destination);

  if (!sourceEndpoint || !destinationEndpoint) {
    return {
      status: TopologyPathTraceResponseStatusEnum.NotFound,
      summary: 'The mocked path tracer could not resolve one or both endpoints.',
      reason: 'Try the captured mock pair 10.10.1.58 → 10.40.0.199.'
    };
  }

  const isCapturedCloudToCloudTrace =
    sourceEndpoint.reference.id === '47ef55b1-9c3b-48c3-884e-ab67a0ee5fea' &&
    destinationEndpoint.reference.id === 'd25f9879-57a2-40fa-b19c-d29391b1b557';
  if (isCapturedCloudToCloudTrace) {
    return mockTraceResponseWithPolicyInputs(capturedCloudToCloudTraceResponse, request);
  }

  const isCapturedCloudToOnPremTrace =
    sourceEndpoint.reference.id === '47ef55b1-9c3b-48c3-884e-ab67a0ee5fea' &&
    destinationEndpoint.reference.id === '10.110.120.3';
  if (isCapturedCloudToOnPremTrace) {
    return mockTraceResponseWithPolicyInputs(capturedCloudToOnPremTraceResponse, request);
  }

  const isCapturedOnPremToCloudTrace =
    sourceEndpoint.reference.id === '10.110.120.3' &&
    destinationEndpoint.reference.id === '9c43f6e5-c471-49c6-bd69-51bad3a1aa44';
  if (isCapturedOnPremToCloudTrace) {
    return mockTraceResponseWithPolicyInputs(capturedOnPremToCloudTraceResponse, request);
  }

  return {
    status: TopologyPathTraceResponseStatusEnum.NotFound,
    summary: 'This mock mode currently exposes only captured path-trace responses.',
    reason: 'Use 10.10.1.58 → 10.40.0.199, 10.10.1.58 → 10.110.120.3, or 10.110.120.3 → 10.10.0.145.'
  };
};

const mockTraceResponseWithPolicyInputs = (
  response: TopologyPathTraceResponse,
  request: TopologyPathTraceRequest
): TopologyPathTraceResponse => {
  const selector = request.trafficSelector;
  const requestedEvidenceLevel = request.desiredEvidenceLevel;
  if (!selector && !requestedEvidenceLevel) {
    return response;
  }

  const details = [
    selector?.protocol ? `protocol ${selector.protocol.toUpperCase()}` : null,
    selector?.destinationPort !== undefined ? `destination port ${selector.destinationPort}` : null,
    requestedEvidenceLevel ? `evidence ${requestedEvidenceLevel}` : null
  ].filter((value): value is string => Boolean(value));
  const message = `Mock received ${details.join(', ')}.`;
  const path = response.path
    ? {
        ...response.path,
        diagnostics: [...(response.path.diagnostics ?? []), { code: 'unsupported_context' as const, message }]
      }
    : response.path;

  return {
    ...response,
    summary: [response.summary, message].filter(Boolean).join(' '),
    path
  };
};
